/**
 * Estimate spoken duration in milliseconds based on text length.
 *
 * In Spanish broadcast speech the average rate is ~150 words per minute.
 * Average Spanish word ≈ 5 characters, which gives ~750 chars/min ≈ 12.5 chars/sec.
 * We use 80 ms per character as a practical broadcast metric (≈ 12.5 chars/s).
 *
 * A minimum floor of 2 000 ms (2 s) prevents absurdly short estimates for
 * very short text fragments, and a maximum cap of 120 000 ms (2 min) avoids
 * inflated estimates from pasted walls of text that wouldn't be read verbatim.
 *
 * @param text  The text whose spoken duration is being estimated.
 * @returns     Duration in milliseconds.
 */

const MS_PER_CHAR = 80; // ~12.5 chars per second — typical Spanish broadcast pace
const MIN_DURATION_MS = 2000; // 2 seconds floor
const MAX_DURATION_MS = 120_000; // 2 minutes cap

export function estimateSpokenDurationMs(text?: string | null): number {
  if (!text || text.trim().length === 0) return MIN_DURATION_MS;

  const charCount = text.trim().length;
  const estimated = charCount * MS_PER_CHAR;

  return Math.max(MIN_DURATION_MS, Math.min(MAX_DURATION_MS, estimated));
}
